import React, { useState, useEffect } from 'react';
import { cleanHtml } from '../utils/sanitize';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { markerIconFor, ContactMapPopup } from '../lib/carteMarkers';
import { getAntenne, deleteAntenne, setAuthToken, getUploadsUrl } from '../services/api';
import { toast } from 'react-toastify';
import Loading from '../components/Loading';
import SuggestionsRattachement from '../components/SuggestionsRattachement';
import { Gate } from '../lib/perms';
import './AntenneDetail.css';

// Qualification labels
const getQualificationLabel = (qualification) => {
  const labels = {
    'antenne_principale': 'Antenne actuelle',
    'antenne_binome': 'Binôme',
    'historique_antenne': 'Ancienne antenne',
    'profil_intéressant': 'Profil intéressant'
  };
  return labels[qualification] || qualification || 'Contact';
};

// Qualification CSS class
const getQualificationClass = (qualification) => {
  const classes = {
    'antenne_principale': 'qualification-principale',
    'antenne_binome': 'qualification-binome',
    'historique_antenne': 'qualification-historique',
    'profil_intéressant': 'qualification-interessant'
  };
  return classes[qualification] || 'qualification-default';
};

// Detect borne type from number
const getBorneTypes = (bornes) => {
  const types = { classik: false, spherik: false, prestige: false };
  if (!bornes || !Array.isArray(bornes)) return types;

  bornes.forEach(borne => {
    const numero = (borne.numero_formated || borne.numero || '').toLowerCase();
    if (numero.startsWith('c')) types.classik = true;
    if (numero.startsWith('s')) types.spherik = true;
    if (numero.startsWith('p')) types.prestige = true;
  });
  return types;
};

function AntenneDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const [antenne, setAntenne] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (auth.user?.access_token) {
      setAuthToken(auth.user.access_token);
      loadAntenne();
    }
  }, [auth.user, id]);

  // silent : rechargement après un rattachement, sans repasser par l'écran de chargement
  const loadAntenne = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const data = await getAntenne(id);
      setAntenne(data);
    } catch (error) {
      console.error('Error loading antenne:', error);
      toast.error('Erreur lors du chargement de l\'antenne');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette antenne ?')) {
      return;
    }

    try {
      setDeleting(true);
      await deleteAntenne(id);
      toast.success('Antenne supprimée avec succès');
      navigate('/antennes');
    } catch (error) {
      console.error('Error deleting antenne:', error);
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return <Loading />;
  }

  if (!antenne) {
    return (
      <div className="not-found">
        <h2>Antenne non trouvée</h2>
        <Link to="/antennes" className="btn btn-primary">Retour à la liste</Link>
      </div>
    );
  }

  const hasLocation = antenne.latitude && antenne.longitude;
  const position = hasLocation ? [parseFloat(antenne.latitude), parseFloat(antenne.longitude)] : null;

  return (
    <div className="antenne-detail">
      <header className="page-header">
        <div className="header-info">
          <Link to="/antennes" className="back-link">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 18l-6-6 6-6" />
            </svg>
            Retour
          </Link>
          <h1>Information ville</h1>
          <p className="breadcrumb">Villes principales / {antenne.ville_principale || 'Détail'}</p>
        </div>
        <div className="header-actions">
          <Gate perm="antennes.edit">
            <Link to={`/antennes/${id}/edit`} className="btn btn-secondary">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              Modifier
            </Link>
          </Gate>
          <Gate perm="antennes.delete">
            <button onClick={handleDelete} disabled={deleting} className="btn btn-danger">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
              {deleting ? 'Suppression...' : 'Supprimer'}
            </button>
          </Gate>
        </div>
      </header>

      <div className="detail-grid">
        {/* Info Section */}
        <section className="detail-card info-section">
          <h2 className="section-title">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12" y2="8" />
            </svg>
            Informations site internet
          </h2>

          <div className="info-grid">
            <div className="info-item">
              <label>ID WordPress</label>
              <span>{antenne.id_wp || '-'}</span>
            </div>
            <div className="info-item">
              <label>Ville exacte</label>
              <span>{antenne.ville_exacte || antenne.ville_principale || '-'}</span>
            </div>
            <div className="info-item">
              <label>Secteur Géographique</label>
              <span>{antenne.secteur_nom || '-'}</span>
            </div>
            <div className="info-item">
              <label>État</label>
              <span className={`badge badge-${antenne.statut ? 'success' : 'warning'}`}>
                {antenne.etat_nom || '-'}
              </span>
            </div>
            <div className="info-item">
              <label>Débit Internet</label>
              <span>{antenne.debit_nom || '-'}</span>
            </div>
            <div className="info-item">
              <label>Type de lieu</label>
              <span>{antenne.lieu_type_nom || '-'}</span>
            </div>
            <div className="info-item">
              <label>Adresse</label>
              <span>{antenne.adresse || '-'}</span>
            </div>
            <div className="info-item">
              <label>Code postal</label>
              <span>{antenne.cp || '-'}</span>
            </div>
            <div className="info-item">
              <label>Pays</label>
              <span>{antenne.pays_nom || '-'}</span>
            </div>
            <div className="info-item">
              <label>Email commercial</label>
              <span>{antenne.email_commercial ? (
                <a href={`mailto:${antenne.email_commercial}`}>{antenne.email_commercial}</a>
              ) : '-'}</span>
            </div>
            <div className="info-item">
              <label>Téléphone</label>
              <span>{antenne.telephone || '-'}</span>
            </div>
            <div className="info-item">
              <label>Convention signée</label>
              <span>{antenne.convention_signe ? 'Oui' : 'Non'}</span>
            </div>
            <div className="info-item">
              <label>Fond vert</label>
              <span>{antenne.fond_vert ? 'Oui' : 'Non'}</span>
            </div>
            {antenne.horaire_accueil && (
              <div className="info-item">
                <label>Horaire d'accueil</label>
                <span>{antenne.horaire_accueil}</span>
              </div>
            )}
            {antenne.horaire_dispos && (
              <div className="info-item">
                <label>Horaire de disponibilité</label>
                <span>{antenne.horaire_dispos}</span>
              </div>
            )}
            {antenne.info_debit && (
              <div className="info-item">
                <label>Info débit</label>
                <span>{antenne.info_debit}</span>
              </div>
            )}
          </div>

          {antenne.commentaire && (
            <div className="info-item full-width">
              <label>Commentaire</label>
              <div className="comment-content" dangerouslySetInnerHTML={{ __html: cleanHtml(antenne.commentaire) }} />
            </div>
          )}
        </section>
        {/* Contacts Section */}
        <section className="detail-card contacts-section">
          <h2 className="section-title">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            {antenne.ville_principale} : contact(s) rattaché(s) ({antenne.contacts?.length || 0})
          </h2>

          {antenne.contacts && antenne.contacts.length > 0 ? (
            <div className="contacts-grid">
              {antenne.contacts.map(contact => {
                const borneTypes = getBorneTypes(contact.bornes);
                const avatarUrl = contact.photo
                  ? getUploadsUrl(contact.photo)
                  : contact.civilite === 'm'
                    ? '/img/avatar-homme.svg'
                    : '/img/avatar-femme.svg';

                return (
                  <div key={contact.id} className="contact-card-wrapper">
                    <div className={`contact-card-header ${getQualificationClass(contact.qualification)}`}>
                      <span>{getQualificationLabel(contact.qualification)}</span>
                    </div>
                    <div className="contact-card-body">
                      <div className="contact-card-layout">
                        <div className="contact-card-photo">
                          <Link to={`/contacts/${contact.id}`}>
                            <img src={avatarUrl} alt={`${contact.prenom} ${contact.nom}`} />
                          </Link>
                        </div>
                        <div className="contact-card-info">
                          <Link to={`/contacts/${contact.id}`} className="contact-card-name">
                            {contact.prenom} {contact.nom}
                          </Link>
                          <p className="contact-card-company">{contact.raison_sociale}</p>

                          {contact.ne_plus_contacter && (
                            <p className="contact-warning">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10" />
                                <line x1="12" y1="8" x2="12" y2="12" />
                                <line x1="12" y1="16" x2="12.01" y2="16" />
                              </svg>
                              Ne plus contacter
                            </p>
                          )}

                          <div className="contact-card-badges">
                            {borneTypes.classik && <span className="badge badge-classik">Antenne Classik</span>}
                            {borneTypes.spherik && <span className="badge badge-spherik">Antenne Spherik</span>}
                            {borneTypes.prestige && <span className="badge badge-prestige">Antenne Prestige</span>}

                            {contact.type_profils?.map(tp => (
                              <span key={tp.id} className="badge badge-profil">{tp.nom}</span>
                            ))}
                          </div>

                          {contact.bornes && contact.bornes.length > 0 && (
                            <p className="contact-card-bornes">
                              Borne{contact.bornes.length > 1 ? 's' : ''} : {contact.bornes.map((b, i) => (
                                <span key={b.id}>
                                  {i > 0 && ', '}
                                  <Link to={`/bornes/${b.id}`}>{b.numero_formated || b.numero}</Link>
                                </span>
                              ))}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="no-data">Aucun contact associé</p>
          )}

          <Link to={`/contacts/new?antenne_id=${id}`} className="btn btn-primary btn-sm mt-1">
            + Ajouter un contact
          </Link>
        </section>

        {/* Suggestions : même ville, non rattachés — carte séparée des contacts rattachés */}
        <SuggestionsRattachement
          antenneId={id}
          ville={antenne.ville_principale || antenne.ville_exacte || 'cette ville'}
          onRattache={() => loadAntenne(true)}
        />


        {/* Map Section */}
        {hasLocation && (
          <section className="detail-card map-section">
            <h2 className="section-title">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              Localisation
            </h2>
            <div className="map-container">
              <MapContainer center={position} zoom={13} scrollWheelZoom={false}>
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <Marker position={position}>
                  <Popup>
                    <strong>{antenne.ville_principale}</strong><br />
                    {antenne.adresse}
                  </Popup>
                </Marker>

                {/* Contacts de l'antenne : mêmes marqueurs et mêmes bulles que
                    la page Carte (lib/carteMarkers), pour ne pas avoir deux
                    représentations du même objet. */}
                {(antenne.contacts || [])
                  .filter((c) => c.addr_lat && c.addr_lng)
                  .map((c) => (
                    <Marker
                      key={c.id}
                      position={[parseFloat(c.addr_lat), parseFloat(c.addr_lng)]}
                      icon={markerIconFor(c, c.bornes_list || [])}
                    >
                      <Popup>
                        <ContactMapPopup
                          contact={c}
                          bornes={c.bornes_list || []}
                          lienProfil={(
                            <Link to={`/contacts/${c.id}`} className="popup-link">Voir le profil →</Link>
                          )}
                        />
                      </Popup>
                    </Marker>
                  ))}
              </MapContainer>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

export default AntenneDetail;
